import { Banner, Spinner } from "@shopify/polaris";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import "../adminWorkspace.css";
import Dashboard from "./dashboard";
import ErrorLog from "./errorLog";
import ShopLinks from "./shopLinks";
import AdminAccounts from "./adminAccounts";
import StorePerformance from "./storePerformance";
import { admin_context } from "../api";

const allPermissions = [
  "shops.view", "shops.manage", "performance.view", "performance.export",
  "logs.view", "logs.delete", "accounts.manage",
];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    let active = true;
    admin_context({}).then((response) => {
      if (active && response.status === 200) setContext(response.data);
      else if (active) setLoadError(true);
    }).catch(() => active && setLoadError(true));
    return () => { active = false; };
  }, []);

  const permissions = useMemo(() => context?.permissions || [], [context?.permissions]);
  const isOwner = Boolean(context?.is_owner);
  const can = useCallback((permission) => isOwner || permissions.includes(permission), [isOwner, permissions]);
  const tabs = useMemo(() => [
    { id: "Dashboard", content: "Shop settings", allowed: can("shops.view") },
    { id: "Performance", content: "Store performance", allowed: can("performance.view") },
    { id: "ErrorLog", content: "Error logs", allowed: can("logs.view") },
    { id: "Accounts", content: "Accounts & permissions", allowed: can("accounts.manage") },
    { id: "ShopLinks", content: "A/B shop links", allowed: isOwner },
  ].filter((tab) => tab.allowed), [can, isOwner]);

  const activeTab = tabs.find((tab) => `/${tab.id}` === location.pathname);
  useEffect(() => {
    if (!context) return;
    if (!activeTab) navigate(tabs.length ? `/${tabs[0].id}` : "/NoAccess", { replace: true });
  }, [activeTab, context, navigate, tabs]);

  if (loadError) return <Banner tone="critical" title="Unable to load administrator permissions. Please sign in again." />;
  if (!context) return <Spinner accessibilityLabel="Loading administrator permissions" />;
  if (!tabs.length) return <Banner title="No super-admin features are authorized for this account." />;

  return (
    <div className="sa-shell">
      <header className="sa-shell-header">
        <div className="sa-brand"><span className="sa-brand-mark" aria-hidden="true" />NinaCheck<span>Super admin</span></div>
        <div className="sa-profile"><span className="sa-user-avatar" aria-hidden="true">{(context.admin?.username || "A").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</span>
          <div><strong>{context.admin?.username || "Administrator"}</strong><small>{isOwner ? "System owner" : "Administrator"}</small></div>
        </div>
      </header>
      <nav className="sa-navigation" aria-label="Super-admin navigation">{tabs.map((tab) => <button type="button" key={tab.id} aria-current={activeTab?.id === tab.id ? "page" : undefined} onClick={() => navigate(`/${tab.id}`)}>{tab.content}</button>)}</nav>
      <Routes>
        <Route path="/" element={<Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Dashboard" element={can("shops.view") ? <Dashboard canManage={can("shops.manage")} canCreate={Boolean(context.can_create_shops)} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Performance" element={can("performance.view") ? <StorePerformance /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/ErrorLog" element={can("logs.view") ? <ErrorLog canDelete={can("logs.delete")} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Accounts" element={can("accounts.manage") ? <AdminAccounts context={context} permissions={allPermissions} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/ShopLinks" element={isOwner ? <ShopLinks /> : <Navigate to={`/${tabs[0].id}`} replace />} />
      </Routes>
    </div>
  );
}
